import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext();

const STORAGE_KEY = "4k_cart_v1";
const REGION_KEY = "4k_region";
const REGION_MANUAL_KEY = "4k_region_manual"; // set when user explicitly chose region
const DISCOUNT_CODE_KEY = "4k_discount_code";
const AUTO_CODE_APPLIED_KEY = "4k_auto_code_applied"; // one-shot flag for welcome banner

// Read `?code=XYZ` (or `?discount=XYZ` / `?promo=XYZ`) from URL exactly once.
const readAutoCodeFromURL = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = (
      params.get("code") ||
      params.get("discount") ||
      params.get("promo") ||
      ""
    ).trim().toUpperCase();
    return code || null;
  } catch {
    return null;
  }
};

// Auto-detect region from browser locale + timezone (EU-friendly, DSGVO-safe, no external API).
// Returns "eu" for DE/AT users, "ch" otherwise.
const detectRegion = () => {
  try {
    const langs = [
      ...(navigator.languages || []),
      navigator.language,
    ].filter(Boolean).map((l) => l.toLowerCase());
    // Explicit CH match wins → CHF
    if (langs.some((l) => l.endsWith("-ch") || l === "de-ch" || l === "fr-ch" || l === "it-ch")) return "ch";
    // Explicit DE/AT locale
    if (langs.some((l) => l === "de-de" || l === "de-at" || l.endsWith("-de") || l.endsWith("-at"))) return "eu";
    // Timezone-based fallback
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Europe/Berlin" || tz === "Europe/Vienna") return "eu";
    if (tz === "Europe/Zurich") return "ch";
  } catch {
    // ignore
  }
  return "ch"; // default: Switzerland
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState(() => {
    // Priority: URL param → localStorage → empty
    return (
      readAutoCodeFromURL() ||
      localStorage.getItem(DISCOUNT_CODE_KEY) ||
      ""
    );
  });
  const [autoCodeBanner, setAutoCodeBanner] = useState(() => {
    // Show welcome banner if code came from URL and hasn't been dismissed for this code yet
    const urlCode = readAutoCodeFromURL();
    if (!urlCode) return null;
    const alreadyShown = localStorage.getItem(AUTO_CODE_APPLIED_KEY) === urlCode;
    if (alreadyShown) return null;
    return urlCode;
  });
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [region, setRegionState] = useState(() => {
    // If user previously chose region manually → honor it
    const manual = localStorage.getItem(REGION_MANUAL_KEY);
    const stored = localStorage.getItem(REGION_KEY);
    if (manual === "1" && stored) return stored;
    return detectRegion();
  });
  const [autoRegionApplied, setAutoRegionApplied] = useState(() => {
    return localStorage.getItem(REGION_MANUAL_KEY) !== "1" && detectRegion() === "eu";
  });

  // Setter that flags region as manually chosen
  const setRegion = useCallback((next) => {
    setRegionState(next);
    localStorage.setItem(REGION_MANUAL_KEY, "1");
    setAutoRegionApplied(false);
  }, []);

  const dismissAutoRegion = useCallback(() => {
    localStorage.setItem(REGION_MANUAL_KEY, "1");
    setAutoRegionApplied(false);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(REGION_KEY, region);
  }, [region]);

  // Persist discount code across visits (so returning FCU15-users still see rabatt)
  useEffect(() => {
    if (discountCode) {
      localStorage.setItem(DISCOUNT_CODE_KEY, discountCode);
    } else {
      localStorage.removeItem(DISCOUNT_CODE_KEY);
    }
  }, [discountCode]);

  const dismissAutoCodeBanner = useCallback(() => {
    if (autoCodeBanner) {
      localStorage.setItem(AUTO_CODE_APPLIED_KEY, autoCodeBanner);
    }
    setAutoCodeBanner(null);
  }, [autoCodeBanner]);

  const addItem = useCallback((product, size) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.slug === product.slug && i.size === size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(next[idx].quantity + 1, 20) };
        return next;
      }
      return [
        ...prev,
        {
          slug: product.slug,
          name: product.name,
          image: product.image,
          price_chf: product.price_chf,
          size,
          quantity: 1,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((slug, size) => {
    setItems((prev) => prev.filter((i) => !(i.slug === slug && i.size === size)));
  }, []);

  const setQty = useCallback((slug, size, qty) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.slug === slug && i.size === size
            ? { ...i, quantity: Math.max(0, Math.min(20, qty)) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setDiscountCode("");
    setQuote(null);
  }, []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  const refreshQuote = useCallback(
    async (code) => {
      if (items.length === 0) {
        setQuote(null);
        return;
      }
      setQuoteLoading(true);
      try {
        const payload = {
          items: items.map((i) => ({ slug: i.slug, size: i.size, quantity: i.quantity })),
          discount_code: code !== undefined ? code : discountCode || null,
          origin_url: window.location.origin,
          region,
        };
        const res = await axios.post(`${API}/checkout/quote`, payload);
        setQuote(res.data);
      } catch (err) {
        console.error("quote error", err);
      } finally {
        setQuoteLoading(false);
      }
    },
    [items, discountCode, region]
  );

  // Auto-refresh quote when items or region change
  useEffect(() => {
    refreshQuote(discountCode || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, region]);

  const value = {
    items,
    open,
    setOpen,
    addItem,
    removeItem,
    setQty,
    clear,
    itemCount,
    discountCode,
    setDiscountCode,
    quote,
    quoteLoading,
    refreshQuote,
    region,
    setRegion,
    autoRegionApplied,
    dismissAutoRegion,
    autoCodeBanner,
    dismissAutoCodeBanner,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
